import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isAxiosError } from "axios";
import { queue } from "@/components/Toast";
import { axiosInstance, getAiRequestErrorMessage } from "@/lib/api";
import { POLL_CURATION_MS } from "@/lib/realtime";
import {
  emitWorkshopTyping,
  joinWorkshopThread,
  useRealtimeStore,
  useWorkshopLiveFrame,
} from "@/lib/socket";
import type {
  ChatMessage,
  ChatToolEvent,
  ChatTurnStatus,
  PersistedChatMessage,
  StreamingToolEvent,
} from "@/types/chat.type";
import type { WorkshopAgentActivity, WorkshopLiveFrame } from "@/types/realtime.type";
import type { WorkshopPresentPerson, WorkshopQuickCreateRef } from "@/types/workshop.type";
import { quickCreatePayload, refusedPick } from "../quickCreate";
import {
  oldestStored,
  prependOlder,
  threadPageUrl,
  trimThread,
  type ThreadPageResponse,
} from "./threadPages";
import {
  createStreamingTurnBuilder,
  type StreamingBlock,
} from "../../components/streamingTurnBlocks";
import {
  buildBlocksFromMessage,
  isTurnStale,
  persistedToChatMessage,
} from "../../components/resumeChatTurn";

/** A typing mark lasts this long without a refresh (the sender refreshes every 3s). */
const TYPING_TTL_MS = 6_000;
/** How often this tab re-announces that its person is still typing. */
const TYPING_REFRESH_MS = 3_000;
/** Stop announcing typing this long after the last keystroke. */
const TYPING_IDLE_MS = 4_000;
/**
 * The socket is how a shared thread stays current; this slow reload is only a
 * net for what it missed (a dropped connection, a restarted server).
 */
const SAFETY_RELOAD_MS = POLL_CURATION_MS;

/** Copy for a run that ended without a reply (the row has no text of its own). */
const NOTICE_TEXT: Record<"budget" | "access" | "error", string> = {
  budget:
    "_I couldn't reply: nobody whose message I was answering has AI budget left this month._",
  access:
    "_I couldn't reply: nobody whose message I was answering can still act in this chat._",
  error: "_I couldn't reply: something went wrong on my side. Send your message again to retry._",
};

type LiveSegment = {
  messageId: string;
  /** The row's: where the live reply sits in the transcript. */
  createdAt: string;
  /**
   * What the row held when this tab picked it up mid-reply (rebuilt from the
   * row, then frozen): the live frames build on after it.
   */
  prefix: StreamingBlock[];
  /** The same, as the row had it: what a finished segment is frozen from. */
  base: Pick<ChatMessage, "content" | "metadata"> & { toolEvents: ChatToolEvent[] };
  builder: ReturnType<typeof createStreamingTurnBuilder>;
  /** Text received so far — where the next chunk must start. */
  textLength: number;
  /** The text the frames added after `base`. */
  text: string;
  tools: StreamingToolEvent[];
  /**
   * Chunks that arrived ahead of `textLength` (this tab joined mid-reply, or
   * missed a frame): held until a reload brings the text before them.
   */
  ahead: Array<{ at: number; content: string }>;
};

/**
 * A segment that just ended, as a transcript row, until the reload brings the
 * saved one (same id: the reload replaces it). The next segment starts right
 * after, and clears the live blocks: without this, what the AI did vanishes
 * for a moment and two messages read as one run.
 */
function frozenRow(live: LiveSegment, status: ChatTurnStatus): ChatMessage {
  const endedAt = new Date().toISOString();
  return {
    id: live.messageId,
    workflowRunId: "",
    role: "assistant",
    content: live.base.content + live.text,
    metadata: live.base.metadata,
    createdAt: live.createdAt,
    toolEvents: [
      ...live.base.toolEvents,
      ...live.tools.map((event) => ({
        toolName: event.toolName,
        toolCallId: event.toolCallId,
        input: event.input,
        result: event.result,
        startedAt: event.startedAt,
        endedAt: event.endedAt || endedAt,
        textOffset: event.textOffset,
      })),
    ],
    status,
  };
}

export type SharedThreadViewer = { id: string; name: string | null };

/** A persisted row as the rail renders it, sender and notices included. */
function toChatMessage(row: PersistedChatMessage): ChatMessage {
  const message = persistedToChatMessage(row);
  const notice = row.metadata?.notice;
  return {
    ...message,
    content: notice && !row.content.trim() ? NOTICE_TEXT[notice] : message.content,
    authorUserId: row.authorUserId ?? null,
    authorName: row.authorName ?? null,
    seenAt: row.seenAt ?? null,
    respondsTo: row.metadata?.respondsTo?.messageId ?? null,
  };
}

/** A card answer refused because someone answered first: the server's sentence. */
function cardTakenMessage(error: unknown): string | null {
  if (!isAxiosError(error) || error.response?.status !== 409) return null;
  const data = error.response.data as { code?: string; message?: string } | undefined;
  return data?.code === "card_already_answered" ? (data.message ?? "Someone already answered this.") : null;
}

/** Placeholder ids for sends the server has not confirmed yet. */
const LOCAL_PREFIX = "local-";

const isLocalSend = (m: ChatMessage) => m.id.startsWith(LOCAL_PREFIX);

/** The stored row a send placeholder stands for. */
const isSameSend = (local: ChatMessage, row: ChatMessage) =>
  row.role === "user" &&
  !isLocalSend(row) &&
  row.authorUserId === local.authorUserId &&
  row.content === local.content;

/**
 * The segment still being written, wherever it sits: people keep sending while
 * it streams, so it is not always the last row. A stale one (its run died)
 * stays in the history as it is.
 */
function findLiveRow(rows: PersistedChatMessage[]): PersistedChatMessage | null {
  for (let i = rows.length - 1; i >= 0; i--) {
    const row = rows[i];
    if (row.role === "assistant" && row.status === "streaming" && !isTurnStale(row.updatedAt)) {
      return row;
    }
  }
  return null;
}

/**
 * A snapshot as the transcript, without losing what reached this tab after the
 * snapshot was requested: messages delivered since (`arrivals` newer than
 * `since`), a "seen" newer than it, and sends still in flight. A snapshot is
 * read before its response lands, so on its own it can be older than the tab.
 * `onConfirmed` gets each send placeholder the snapshot turned out to hold.
 */
function mergeSnapshot(
  snapshot: ChatMessage[],
  current: ChatMessage[],
  since: number,
  arrivals: Map<string, number>,
  onConfirmed: (localId: string) => void,
  /**
   * The snapshot is the newest page and starts here, joined onto what is
   * loaded: the older pages read on the way up stay (it says nothing of them).
   */
  pageStart: string | null = null,
): ChatMessage[] {
  const snapshotIds = new Set(snapshot.map((m) => m.id));
  const currentById = new Map(current.map((m) => [m.id, m]));
  // Rows new to this tab: one of them may be a pending send's own row, which
  // takes over the placeholder's key (its bubble stays the same element).
  const unseen = snapshot.filter((row) => !currentById.has(row.id));
  const placeholderOf = new Map<string, string>();
  // In front of the page, in their order: the transcript stays oldest first.
  const older = pageStart
    ? current.filter((m) => !snapshotIds.has(m.id) && !isLocalSend(m) && m.createdAt < pageStart)
    : [];
  const olderIds = new Set(older.map((m) => m.id));
  const kept = current.filter((m) => {
    if (snapshotIds.has(m.id) || olderIds.has(m.id)) return false;
    if (isLocalSend(m)) {
      const row = unseen.find((r) => isSameSend(m, r) && !placeholderOf.has(r.id));
      if (!row) return true;
      placeholderOf.set(row.id, m.id);
      onConfirmed(m.id);
      return false;
    }
    return (arrivals.get(m.id) ?? 0) > since;
  });
  const merged = snapshot.map((row) => {
    const mine = currentById.get(row.id);
    const clientKey = mine?.clientKey ?? placeholderOf.get(row.id);
    const seenAt = mine?.seenAt && !row.seenAt ? mine.seenAt : row.seenAt;
    return clientKey || seenAt !== row.seenAt ? { ...row, seenAt, ...(clientKey ? { clientKey } : {}) } : row;
  });
  return older.length > 0 || kept.length > 0 ? [...older, ...merged, ...kept] : merged;
}

/** A live segment rebuilt from its streaming row (joined mid-reply, or a refetch). */
function segmentFromRow(row: PersistedChatMessage): { live: LiveSegment; blocks: StreamingBlock[] } {
  const prefix = buildBlocksFromMessage(row, { live: false });
  const message = persistedToChatMessage(row);
  return {
    live: {
      messageId: row.id,
      createdAt: row.createdAt,
      prefix,
      base: {
        content: message.content,
        metadata: message.metadata,
        toolEvents: message.toolEvents ?? [],
      },
      builder: createStreamingTurnBuilder(),
      textLength: row.content.length,
      text: "",
      tools: [],
      ahead: [],
    },
    blocks: prefix,
  };
}

/**
 * Applies held chunks that now line up with the text, in order; stops at the
 * first that is still ahead. The segment's blocks, or null when nothing applied.
 */
function applyAhead(live: LiveSegment): StreamingBlock[] | null {
  live.ahead.sort((a, b) => a.at - b.at);
  let built: StreamingBlock[] | null = null;
  while (live.ahead.length > 0 && live.ahead[0].at <= live.textLength) {
    const chunk = live.ahead.shift()!;
    const fresh = chunk.content.slice(live.textLength - chunk.at);
    if (!fresh) continue;
    live.textLength += fresh.length;
    live.text += fresh;
    built = live.builder.onTextChunk(fresh);
  }
  return built ? [...live.prefix, ...built] : null;
}

/** A new, empty segment the socket announced. */
function freshSegment(messageId: string, createdAt: string): LiveSegment {
  return {
    messageId,
    createdAt,
    prefix: [],
    base: { content: "", metadata: null, toolEvents: [] },
    builder: createStreamingTurnBuilder(),
    textLength: 0,
    text: "",
    tools: [],
    ahead: [],
  };
}

/**
 * A shared workshop thread: one AI, several people. Sending never waits for
 * the reply — the message is stored, and the thread's runner answers it (with
 * anything else sent meanwhile) and streams to everyone in the thread room.
 * This hook loads the transcript, joins that room (which is what makes the
 * viewer present), and follows the live reply, typing and presence.
 *
 * What the AI does shows as its tool calls and what it sends; the server never
 * sends its thinking. For the whole of a run, thinking included, the thread
 * shows it working (isReplying); its mark sits on the newest message it read.
 *
 * Inactive while `sessionId` is null.
 */
export function useSharedWorkshopThread(sessionId: string | null, viewer: SharedThreadViewer | null) {
  const socket = useRealtimeStore((s) => s.socket);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadedSessionId, setLoadedSessionId] = useState<string | null>(null);
  const [streamingBlocks, setStreamingBlocks] = useState<StreamingBlock[]>([]);
  const [streamingAt, setStreamingAt] = useState<string | null>(null);
  const [agentState, setAgentState] = useState<{
    state: WorkshopAgentActivity;
    toolName?: string;
  }>({ state: "idle" });
  const [present, setPresent] = useState<WorkshopPresentPerson[]>([]);
  const [typing, setTyping] = useState<Record<string, { name: string | null; until: number }>>({});
  const [isStopping, setIsStopping] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  // Paging (threadPages.ts): older messages exist before the oldest one loaded.
  const [hasOlder, setHasOlder] = useState(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const loadingOlderRef = useRef(false);
  /** Pages older than the newest one are loaded (read while scrolled up). */
  const olderLoadedRef = useRef(false);
  /** The transcript as this tab last rendered it, for paging decisions outside a render. */
  const messagesRef = useRef<ChatMessage[]>([]);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const liveRef = useRef<LiveSegment | null>(null);
  /**
   * Whether the last word from the server was that a run is going (thinking
   * included): the thread shows the assistant working for all of it. The ref
   * says when a finished run needs a reload.
   */
  const replyingRef = useRef(false);
  const [runActive, setRunActive] = useState(false);
  const loadSeq = useRef(0);
  const sessionRef = useRef(sessionId);
  sessionRef.current = sessionId;
  /**
   * Messages that reached this tab outside a snapshot (the socket, a send's
   * response), in arrival order: a snapshot requested before one of them
   * cannot know it, so it must not drop it.
   */
  const arrivalSeq = useRef(0);
  const arrivals = useRef(new Map<string, number>());
  /** Send placeholders already known to be stored, so a late failure is not reported. */
  const confirmedSends = useRef(new Set<string>());
  const localSendSeq = useRef(0);

  const noteArrival = useCallback((messageId: string) => {
    arrivals.current.set(messageId, ++arrivalSeq.current);
  }, []);
  /** Set once `reloadSoon` exists; `load` schedules a follow-up through it. */
  const reloadSoonRef = useRef<() => void>(() => {});

  const setLive = useCallback((live: LiveSegment | null, blocks: StreamingBlock[]) => {
    liveRef.current = live;
    setStreamingAt(live?.createdAt ?? null);
    setStreamingBlocks(blocks);
  }, []);

  /**
   * (Re)loads the transcript and merges it with what the socket has delivered
   * since it was requested. A reply still being written comes back as a
   * streaming row; it becomes the live segment unless the socket is already
   * following that same row.
   */
  const load = useCallback(async () => {
    const id = sessionRef.current;
    if (!id) return;
    const seq = ++loadSeq.current;
    const since = arrivalSeq.current;
    try {
      // The newest page: older pages are read as the thread is scrolled up.
      const res = await axiosInstance.get<ThreadPageResponse>(threadPageUrl(id));
      if (seq !== loadSeq.current || sessionRef.current !== id) return;
      const live = liveRef.current;
      const liveRow = findLiveRow(res.data.messages);
      // The socket already moved on to a later segment: this snapshot was read
      // before it started, and its streaming row is behind the tab.
      const rowIsBehind =
        !!liveRow && !!live && live.messageId !== liveRow.id && live.createdAt > liveRow.createdAt;
      const snapshot = res.data.messages.filter((m) => m !== liveRow).map(toChatMessage);
      // The page joins onto what is loaded (shares a message with it): the
      // older pages read on the way up stay. Otherwise the window starts over.
      const pageHasMore = Boolean(res.data.hasMore);
      const pageIds = new Set(res.data.messages.map((m) => m.id));
      const joins =
        pageHasMore &&
        res.data.messages.length > 0 &&
        messagesRef.current.some((m) => pageIds.has(m.id));
      const pageStart = joins ? res.data.messages[0].createdAt : null;
      setMessages((current) =>
        mergeSnapshot(
          snapshot,
          current,
          since,
          arrivals.current,
          (localId) => confirmedSends.current.add(localId),
          pageStart,
        ),
      );
      if (!joins) olderLoadedRef.current = false;
      // Older pages already read keep their own "more before them".
      if (!olderLoadedRef.current) setHasOlder(pageHasMore);
      if (liveRow && !rowIsBehind && (!live || live.messageId !== liveRow.id)) {
        const rebuilt = segmentFromRow(liveRow);
        setLive(rebuilt.live, rebuilt.blocks);
      } else if (liveRow && live?.messageId === liveRow.id && live.ahead.length > 0) {
        // Catching up on a reply this tab joined late or lost a frame of: take
        // the row's text when it is further along, then the chunks held back.
        if (liveRow.content.length > live.textLength) {
          const rebuilt = segmentFromRow(liveRow);
          rebuilt.live.ahead = live.ahead;
          setLive(rebuilt.live, applyAhead(rebuilt.live) ?? rebuilt.blocks);
        }
        // The row is saved about once a second: still short, look again soon.
        if (liveRef.current?.ahead.length) reloadSoonRef.current();
      } else if (!liveRow && live) {
        // Drop the live blocks only once this snapshot has their row in the
        // history (final, or its run died). A snapshot taken before the row
        // existed says nothing about it.
        if (res.data.messages.some((m) => m.id === live.messageId)) setLive(null, []);
      }
      setLoadFailed(false);
    } catch (error) {
      if (seq === loadSeq.current && sessionRef.current === id) {
        console.error("Failed to load the shared thread:", error);
        setLoadFailed(true);
      }
    } finally {
      if (seq === loadSeq.current && sessionRef.current === id) setLoadedSessionId(id);
    }
  }, [setLive]);

  // A missed frame (joined mid-reply, a gap) is repaired by one reload.
  const reloadTimer = useRef<number | null>(null);
  const reloadSoon = useCallback(() => {
    if (reloadTimer.current !== null) return;
    reloadTimer.current = window.setTimeout(() => {
      reloadTimer.current = null;
      void load();
    }, 250);
  }, [load]);
  useEffect(() => {
    reloadSoonRef.current = reloadSoon;
  }, [reloadSoon]);
  useEffect(
    () => () => {
      if (reloadTimer.current !== null) window.clearTimeout(reloadTimer.current);
    },
    [],
  );

  // New thread: start clean, then load.
  useEffect(() => {
    arrivals.current = new Map();
    confirmedSends.current = new Set();
    messagesRef.current = [];
    setMessages([]);
    setHasOlder(false);
    olderLoadedRef.current = false;
    setLoadedSessionId(null);
    setLive(null, []);
    setAgentState({ state: "idle" });
    replyingRef.current = false;
    setRunActive(false);
    setPresent([]);
    setTyping({});
    setLoadFailed(false);
    if (sessionId) void load();
  }, [sessionId, load, setLive]);

  /** The page before the oldest message loaded (the thread was scrolled up). */
  const loadOlder = useCallback(async (): Promise<boolean> => {
    const id = sessionRef.current;
    const oldest = oldestStored(messagesRef.current);
    if (!id || !oldest || loadingOlderRef.current) return false;
    loadingOlderRef.current = true;
    setIsLoadingOlder(true);
    try {
      const res = await axiosInstance.get<ThreadPageResponse>(threadPageUrl(id, oldest.id));
      if (sessionRef.current !== id) return false;
      const older = res.data.messages.map(toChatMessage);
      if (older.length > 0) olderLoadedRef.current = true;
      setMessages((current) => prependOlder(current, older));
      setHasOlder(Boolean(res.data.hasMore));
      return true;
    } catch (error) {
      console.error("Failed to load earlier messages:", error);
      return false;
    } finally {
      loadingOlderRef.current = false;
      setIsLoadingOlder(false);
    }
  }, []);

  /** Back at the newest end: let the pages read on the way up go again. */
  const trimOlder = useCallback(() => {
    if (loadingOlderRef.current) return;
    const trimmed = trimThread(messagesRef.current);
    if (!trimmed) return;
    const dropped = new Set(trimmed.dropped.map((m) => m.id));
    setMessages((current) => current.filter((m) => !dropped.has(m.id)));
    olderLoadedRef.current = false;
    setHasOlder(true);
  }, []);

  // The net: reload now and then while the thread is on screen.
  useEffect(() => {
    if (!sessionId) return;
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") void load();
    }, SAFETY_RELOAD_MS);
    return () => window.clearInterval(id);
  }, [sessionId, load]);

  // Join the thread's room for as long as it is open: that is what shows the
  // viewer as present. Every join catches up on what the socket could not
  // deliver before it (the first one included: a message sent between the
  // first load and the join reached neither).
  useEffect(() => {
    if (!socket || !sessionId) return;
    return joinWorkshopThread(socket, sessionId, (result) => {
      setPresent(result.presence.users);
      replyingRef.current = result.presence.replying;
      setRunActive(result.presence.replying);
      setAgentState({ state: result.agentState });
      void load();
    });
  }, [socket, sessionId, load]);

  // Typing marks fade unless refreshed.
  const typingCount = Object.keys(typing).length;
  useEffect(() => {
    if (typingCount === 0) return;
    const id = window.setInterval(() => {
      const now = Date.now();
      setTyping((current) => {
        const next = Object.fromEntries(
          Object.entries(current).filter(([, mark]) => mark.until > now),
        );
        return Object.keys(next).length === Object.keys(current).length ? current : next;
      });
    }, 1_000);
    return () => window.clearInterval(id);
  }, [typingCount]);

  const applyStreamFrame = useCallback(
    (frame: Extract<WorkshopLiveFrame, { type: "stream" }>) => {
      const live = liveRef.current;
      if (!live || live.messageId !== frame.messageId) {
        // A segment that started before this tab was listening.
        reloadSoon();
        return;
      }
      const f = frame.frame as {
        type?: string;
        content?: string;
        toolName?: string;
        toolCallId?: string;
        toolInput?: unknown;
        result?: unknown;
      };
      if (f.type === "chunk" && typeof f.content === "string") {
        const have = live.textLength;
        const at = frame.offset ?? have;
        if (at > have) {
          // Text before this chunk never reached the tab: hold the chunk, and
          // let a reload bring what came before it.
          live.ahead.push({ at, content: f.content });
          reloadSoon();
          return;
        }
        // Frames may repeat what a reload already brought in: keep only the new tail.
        const fresh = f.content.slice(have - at);
        if (!fresh) return;
        live.textLength += fresh.length;
        live.text += fresh;
        const built = live.builder.onTextChunk(fresh);
        setStreamingBlocks(applyAhead(live) ?? [...live.prefix, ...built]);
        return;
      }
      if (f.type === "tool_start" && f.toolName) {
        const event: StreamingToolEvent = {
          toolName: f.toolName,
          toolCallId: f.toolCallId ?? `${f.toolName}-${live.tools.length}`,
          input: f.toolInput ?? null,
          result: null,
          status: "running",
          startedAt: new Date().toISOString(),
          endedAt: "",
          textOffset: live.textLength,
        };
        live.tools.push(event);
        setStreamingBlocks([...live.prefix, ...live.builder.onToolStart(event)]);
        return;
      }
      if (f.type === "tool_end" && f.toolName) {
        const event = live.tools.find(
          (e) => (f.toolCallId ? e.toolCallId === f.toolCallId : e.toolName === f.toolName) &&
            e.status === "running",
        );
        if (!event) return;
        event.status = "completed";
        event.result = f.result ?? null;
        event.endedAt = new Date().toISOString();
        setStreamingBlocks([
          ...live.prefix,
          ...live.builder.onToolEnd(event.toolCallId, {
            result: event.result,
            status: event.status,
            endedAt: event.endedAt,
          }),
        ]);
      }
    },
    [reloadSoon],
  );

  useWorkshopLiveFrame((frame) => {
    if (!sessionId || frame.sessionId !== sessionId) return;
    switch (frame.type) {
      case "message.created": {
        const incoming: ChatMessage = {
          id: frame.message.id,
          workflowRunId: "",
          role: "user",
          content: frame.message.content,
          metadata: frame.message.quickCreates?.length ? { quickCreates: frame.message.quickCreates } : null,
          createdAt: frame.message.createdAt,
          authorUserId: frame.message.authorUserId,
          authorName: frame.message.authorName,
          seenAt: null,
          respondsTo: frame.message.respondsTo ?? null,
        };
        noteArrival(incoming.id);
        setMessages((current) => {
          if (current.some((m) => m.id === incoming.id)) return current;
          // Our own send, already shown: swap the placeholder for the real row,
          // which keeps the placeholder's key (its bubble may still be flying).
          const local = current.findIndex((m) => isLocalSend(m) && isSameSend(m, incoming));
          if (local >= 0) {
            confirmedSends.current.add(current[local].id);
            const next = [...current];
            next[local] = { ...incoming, clientKey: current[local].id };
            return next;
          }
          return [...current, incoming];
        });
        return;
      }
      case "messages.seen": {
        const ids = new Set(frame.messageIds);
        setMessages((current) =>
          current.some((m) => ids.has(m.id))
            ? current.map((m) => (ids.has(m.id) ? { ...m, seenAt: frame.seenAt } : m))
            : current,
        );
        return;
      }
      case "segment.started":
        replyingRef.current = true;
        setRunActive(true);
        setLive(freshSegment(frame.messageId, frame.createdAt), []);
        return;
      case "stream":
        applyStreamFrame(frame);
        return;
      case "segment.ended": {
        const live = liveRef.current;
        if (live?.messageId === frame.messageId) {
          if (frame.silent) {
            setLive(null, []);
            return;
          }
          // What it did stays where it was, as a row, until the reload brings
          // the saved one: the next segment's start would otherwise clear it.
          const row = frozenRow(live, frame.status);
          noteArrival(row.id);
          setMessages((current) =>
            current.some((m) => m.id === row.id) ? current : [...current, row],
          );
          setLive(null, []);
        }
        void load();
        return;
      }
      case "run.notice":
        void load();
        return;
      case "agent.state":
        setAgentState({ state: frame.state, toolName: frame.toolName });
        if (frame.state === "idle") setIsStopping(false);
        return;
      case "typing":
        if (frame.userId === viewer?.id) return;
        setTyping((current) => {
          const next = { ...current };
          if (frame.typing) next[frame.userId] = { name: frame.name, until: Date.now() + TYPING_TTL_MS };
          else delete next[frame.userId];
          return next;
        });
        return;
      case "presence":
        setPresent(frame.users);
        if (!frame.replying) {
          setIsStopping(false);
          // The run is over, whatever its last frames said (or failed to say):
          // the assistant is idle, and anything missed is in the transcript.
          setAgentState({ state: "idle" });
          if (replyingRef.current) reloadSoon();
        }
        replyingRef.current = frame.replying;
        setRunActive(frame.replying);
        return;
    }
  });

  // ---- Typing (this person) ----

  const typingState = useRef<{ lastSentAt: number; idleTimer: number | null; typing: boolean }>({
    lastSentAt: 0,
    idleTimer: null,
    typing: false,
  });

  const stopTyping = useCallback(() => {
    const state = typingState.current;
    if (state.idleTimer !== null) window.clearTimeout(state.idleTimer);
    state.idleTimer = null;
    if (state.typing && sessionRef.current) emitWorkshopTyping(sessionRef.current, false);
    state.typing = false;
    state.lastSentAt = 0;
  }, []);

  /** Call with the composer's text on every change. */
  const notifyTyping = useCallback(
    (text: string) => {
      const id = sessionRef.current;
      if (!id) return;
      if (!text.trim()) {
        stopTyping();
        return;
      }
      const state = typingState.current;
      const now = Date.now();
      if (!state.typing || now - state.lastSentAt > TYPING_REFRESH_MS) {
        emitWorkshopTyping(id, true);
        state.typing = true;
        state.lastSentAt = now;
      }
      if (state.idleTimer !== null) window.clearTimeout(state.idleTimer);
      state.idleTimer = window.setTimeout(stopTyping, TYPING_IDLE_MS);
    },
    [stopTyping],
  );

  // Leaving the thread (or the page) ends any typing mark.
  useEffect(() => stopTyping, [sessionId, stopTyping]);

  // ---- Sending and stopping ----

  /**
   * `respondsTo` sends it as the answer to the card on that assistant message.
   * The first answer to a card wins; a later one is refused and not shown.
   */
  const sendMessage = useCallback(
    async (
      content: string,
      opts?: {
        respondsTo?: string;
        quickCreates?: WorkshopQuickCreateRef[];
        /** A pick went: its id (pickId), or "*" when unnamed. */
        onQuickCreateRefused?: (refused: string) => void;
      },
    ): Promise<boolean> => {
      const id = sessionRef.current;
      const trimmed = content.trim();
      const quickCreates = opts?.quickCreates ?? [];
      if (!id || (!trimmed && quickCreates.length === 0)) return false;
      stopTyping();
      const localId = `${LOCAL_PREFIX}${++localSendSeq.current}`;
      setMessages((current) => [
        ...current,
        {
          id: localId,
          workflowRunId: "",
          role: "user",
          content: trimmed,
          metadata: quickCreates.length > 0 ? { quickCreates } : null,
          createdAt: new Date().toISOString(),
          authorUserId: viewer?.id ?? null,
          authorName: viewer?.name ?? null,
          // Closes the card in this tab at once; the server has the final say.
          respondsTo: opts?.respondsTo ?? null,
        },
      ]);
      try {
        const res = await axiosInstance.post<{ message: PersistedChatMessage }>(
          `/workshop/sessions/${id}/messages`,
          {
            message: trimmed,
            ...(opts?.respondsTo ? { respondsTo: opts.respondsTo } : {}),
            ...(quickCreates.length > 0 ? { quickCreates: quickCreatePayload(quickCreates) } : {}),
          },
        );
        // Switched threads meanwhile: this one is not on screen any more.
        if (sessionRef.current !== id) return true;
        const saved = toChatMessage(res.data.message);
        noteArrival(saved.id);
        setMessages((current) => {
          // The socket (or a reload) may have delivered it first.
          if (current.some((m) => m.id === saved.id)) return current.filter((m) => m.id !== localId);
          // In the placeholder's place, under its key: the bubble may still be flying.
          const at = current.findIndex((m) => m.id === localId);
          if (at < 0) return [...current, saved];
          const next = [...current];
          next[at] = { ...saved, clientKey: localId };
          return next;
        });
        return true;
      } catch (error) {
        // Stored after all (the socket delivered it): the request only failed
        // on its way back. Reporting it would invite a duplicate resend.
        if (confirmedSends.current.has(localId)) return true;
        setMessages((current) => current.filter((m) => m.id !== localId));
        const refused = quickCreates.length > 0 ? refusedPick(error) : null;
        if (refused) {
          // A pick went while it sat in the draft: nothing was stored.
          opts?.onQuickCreateRefused?.(refused);
          return false;
        }
        const taken = cardTakenMessage(error);
        if (taken) {
          // Someone else's answer is on its way over the socket; a reload makes sure.
          reloadSoon();
          queue.add({ title: taken, variant: "info" });
          return false;
        }
        queue.add({
          title: "Your message wasn't sent",
          description: getAiRequestErrorMessage(error),
          variant: "warning",
        });
        return false;
      }
    },
    [viewer?.id, viewer?.name, stopTyping, noteArrival, reloadSoon],
  );

  /** Anyone in a shared thread may stop its reply. */
  const stop = useCallback(() => {
    const id = sessionRef.current;
    if (!id || isStopping) return;
    setIsStopping(true);
    axiosInstance
      .post<{ aborted: boolean }>(`/workshop/sessions/${id}/abort`, {})
      .then((res) => {
        // Nothing was running: no frame will come to end the "Stopping…".
        if (!res.data.aborted) {
          setIsStopping(false);
          reloadSoon();
        }
      })
      .catch((error) => {
        setIsStopping(false);
        queue.add({
          title: "Couldn't stop the reply",
          description: getAiRequestErrorMessage(error),
          variant: "warning",
        });
      });
  }, [isStopping, reloadSoon]);

  // Working, from the moment a run starts until it ends: thinking, waiting on
  // the model's provider, running tools, writing. The thread shows it the
  // whole time (never only between the steps it can see).
  const isReplying =
    runActive || streamingAt !== null || streamingBlocks.length > 0 || agentState.state !== "idle";

  // The newest message the assistant has read: the one that carries its mark.
  const lastSeenMessageId = useMemo(() => {
    let last: ChatMessage | null = null;
    for (const m of messages) {
      if (m.role !== "user" || !m.seenAt) continue;
      if (!last || m.createdAt >= last.createdAt) last = m;
    }
    return last?.id ?? null;
  }, [messages]);

  return {
    messages,
    isLoadingMessages: !!sessionId && loadedSessionId !== sessionId,
    /** The last load failed; `reload` tries again. */
    loadFailed,
    reload: load,
    streamingBlocks,
    /** Where the live reply sits in the transcript (its row's createdAt). */
    streamingAt,
    isReplying,
    agentState,
    lastSeenMessageId,
    present,
    typing: Object.entries(typing).map(([userId, mark]) => ({ userId, name: mark.name })),
    sendMessage,
    stop,
    isStopping,
    notifyTyping,
    // Files in a shared thread are placed by author and time, never by claim.
    paging: { hasOlder, isLoadingOlder, loadOlder, trimOlder, userRowOffset: 0 },
  };
}
